import React, { useState, useEffect, useCallback } from 'react';
import {
    FaEnvelope,
    FaSync,
    FaExclamationTriangle,
    FaCheckCircle,
    FaInfoCircle,
    FaChevronDown,
    FaChevronRight,
    FaUserCheck,
    FaUserTimes,
    FaHistory,
} from 'react-icons/fa';
import { MONTHLY_MAIL_LABELS } from '../../utils/constants';
import { getBleepMonthlyMailStatus } from '../../services/onboardingService';

const formatTimestamp = (timestamp) => {
    if (!timestamp || typeof timestamp !== 'number') return 'Not recorded';
    try {
        const d = new Date(timestamp);
        return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} (local time)`;
    } catch {
        return 'Invalid date';
    }
};

const getLabel = (code) => {
    if (!code) return 'Not recorded';
    return MONTHLY_MAIL_LABELS[code] || String(code).replace(/_/g, ' ');
};

const isValidPeriodId = (id) => typeof id === 'string' && /^20\d{2}-(0[1-9]|1[0-2])-23_20\d{2}-(0[1-9]|1[0-2])-23$/.test(id);

const MonthlyEmailAuditSection = ({ serverID, periodID, reportChecksum = '' }) => {
    const [jobs, setJobs] = useState([]);
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);

    const hasValidPeriod = isValidPeriodId(periodID);

    // Accordion open states
    const [openRecipients, setOpenRecipients] = useState(true);
    const [openExclusions, setOpenExclusions] = useState(false);
    const [openPreview, setOpenPreview] = useState(false);
    const [openAudit, setOpenAudit] = useState(false);

    const loadMailStatus = useCallback(async () => {
        if (!serverID || !hasValidPeriod) {
            setJobs([]);
            setLoaded(true);
            setBusy(false);
            return;
        }
        setBusy(true);
        setError(null);

        try {
            const res = await getBleepMonthlyMailStatus(serverID, periodID);
            const returnedJobs = Array.isArray(res?.jobs) ? res.jobs : [];
            setJobs(returnedJobs);
            setLoaded(true);

            // Default select the first job or maintain current selection
            setSelectedJobId((current) => {
                if (returnedJobs.some((j) => j.jobID === current)) return current;
                return returnedJobs[0]?.jobID || null;
            });
        } catch (err) {
            console.error('Failed to load email delivery status:', err);
            const msg = err?.message || 'Could not load monthly email status.';
            setError(msg.includes('permission-denied') ? 'Super Admin access to this division is required.' : msg);
            setJobs([]);
        } finally {
            setBusy(false);
        }
    }, [serverID, periodID, hasValidPeriod]);

    useEffect(() => {
        loadMailStatus();
    }, [loadMailStatus]);

    const activeJob = jobs.find((j) => j.jobID === selectedJobId) || jobs[0] || null;

    const recipients = Array.isArray(activeJob?.recipients) ? activeJob.recipients : [];
    const audit = Array.isArray(activeJob?.audit) ? activeJob.audit : [];
    const excluded = audit.filter((r) => r.decision === 'EXCLUDED');
    const acceptedCount = recipients.filter((r) => r.state === 'SMTP_ACCEPTED').length;
    const reviewCount = recipients.filter((r) => r.state === 'REVIEW_REQUIRED').length;
    const isPreview = activeJob?.dryRun === true;
    const isKnownAudit = activeJob?.auditComplete === true;
    const jobState = activeJob?.state || '';
    const hasAttention = reviewCount > 0 || jobState === 'COMPLETE_WITH_ISSUES';

    let statusTitle = isPreview
        ? (jobState === 'NO_RECIPIENTS' ? 'No eligible recipients' : 'Preview only · no emails sent')
        : getLabel(jobState);

    let statusDescription = isPreview
        ? 'This is a preview for the selected period. It does not indicate whether future scheduled emails are enabled.'
        : jobState === 'NO_RECIPIENTS'
            ? 'No superadmin email addresses met the recipient rules.'
            : `Accepted by the mail server: ${acceptedCount} of ${recipients.length}. Inbox delivery is not confirmed.`;

    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '24px',
            marginTop: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '12px',
                paddingBottom: '16px',
                borderBottom: '1px solid #edf2f7',
                marginBottom: '20px',
            }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{
                        padding: '10px',
                        borderRadius: '8px',
                        backgroundColor: '#E5F2EF',
                        color: '#176B64',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <FaEnvelope style={{ fontSize: '18px' }} />
                    </div>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#2d3748' }}>
                            Monthly Email Delivery Status
                        </h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#718096', maxWidth: '650px', lineHeight: '1.5' }}>
                            Each division’s eligible superadmins receive its monthly summary and CSV. This section shows who was selected and the recorded SMTP delivery status.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={loadMailStatus}
                    disabled={busy}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#edf2f7',
                        color: '#2b6cb0',
                        border: '1px solid #cbd5e0',
                        padding: '8px 14px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: busy ? 'not-allowed' : 'pointer',
                    }}
                    title="Refresh status · does not send email"
                >
                    <FaSync className={busy ? 'fa-spin' : ''} />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#fff5f5',
                    color: '#c53030',
                    border: '1px solid #fed7d7',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                }}>
                    <FaExclamationTriangle />
                    <span>{error}</span>
                </div>
            )}

            {/* Invalid or Unselected Period Prompt */}
            {!hasValidPeriod && (
                <div style={{
                    padding: '30px',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #cbd5e0',
                    color: '#718096',
                }}>
                    <FaInfoCircle style={{ fontSize: '24px', color: '#a0aec0', marginBottom: '8px' }} />
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                        Select a closed monthly period
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#718096' }}>
                        Monthly summary emails are dispatched on the 23rd for closed periods. Select a completed period from the dropdown above to audit delivery logs.
                    </p>
                </div>
            )}

            {/* Empty State */}
            {hasValidPeriod && loaded && !busy && !error && jobs.length === 0 && (
                <div style={{
                    padding: '30px',
                    textAlign: 'center',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #cbd5e0',
                    color: '#718096',
                }}>
                    <FaInfoCircle style={{ fontSize: '24px', color: '#a0aec0', marginBottom: '8px' }} />
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                        No email prepared for this period
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#718096' }}>
                        The monthly schedule creates email jobs automatically at 2:00 AM UK time on the 23rd. Viewing this report does not trigger or send an email.
                    </p>
                </div>
            )}

            {/* Active Job Details */}
            {activeJob && (
                <div>
                    {/* Job Selector Dropdown (if multiple jobs exist) */}
                    {jobs.length > 1 && (
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '700', color: '#4a5568', display: 'block', marginBottom: '6px' }}>
                                Delivery Run:
                            </label>
                            <select
                                value={selectedJobId || ''}
                                onChange={(e) => setSelectedJobId(e.target.value)}
                                style={{
                                    width: '100%',
                                    maxWidth: '380px',
                                    padding: '8px 12px',
                                    fontSize: '13px',
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e0',
                                    backgroundColor: '#ffffff',
                                    outline: 'none',
                                    fontWeight: '600',
                                    color: '#2d3748',
                                }}
                            >
                                {jobs.map((j) => (
                                    <option key={j.jobID} value={j.jobID}>
                                        {j.dryRun ? 'View Preview Job' : 'View Live Delivery Job'} ({j.jobID})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Status Overview Card */}
                    <div style={{
                        padding: '16px 20px',
                        borderRadius: '8px',
                        backgroundColor: hasAttention ? '#FFF8E6' : isPreview ? '#EBF8FF' : '#F0F9F8',
                        border: `1px solid ${hasAttention ? '#FEEBC8' : isPreview ? '#BEE3F8' : '#BFE3DE'}`,
                        marginBottom: '16px',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            {hasAttention ? (
                                <FaExclamationTriangle style={{ color: '#D69E2E' }} />
                            ) : isPreview ? (
                                <FaInfoCircle style={{ color: '#3182CE' }} />
                            ) : (
                                <FaCheckCircle style={{ color: '#176B64' }} />
                            )}
                            <span style={{
                                fontSize: '14px',
                                fontWeight: '700',
                                color: hasAttention ? '#975A16' : isPreview ? '#2B6CB0' : '#176B64',
                            }}>
                                {statusTitle}
                            </span>
                        </div>
                        <p style={{
                            margin: 0,
                            fontSize: '13px',
                            color: hasAttention ? '#7B341E' : isPreview ? '#2C5282' : '#2D3748',
                            lineHeight: '1.5',
                        }}>
                            {statusDescription}
                        </p>
                        {reviewCount > 0 && (
                            <p style={{ margin: '6px 0 0 0', fontSize: '12px', fontWeight: '700', color: '#C53030' }}>
                                ⚠️ {reviewCount} delivery outcome{reviewCount > 1 ? 's need' : ' needs'} review.
                            </p>
                        )}
                    </div>

                    {/* Summary Bar */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                        fontSize: '13px',
                        color: '#4a5568',
                        padding: '8px 12px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px solid #edf2f7',
                        marginBottom: '16px',
                    }}>
                        <div>
                            <strong style={{ color: '#176B64' }}>{recipients.length}</strong> email recipient{recipients.length !== 1 ? 's' : ''} &nbsp;·&nbsp;
                            <strong style={{ color: '#718096' }}>
                                {isKnownAudit ? `${excluded.length} admins excluded` : 'Exclusions not fully recorded'}
                            </strong>
                        </div>
                        {reportChecksum && activeJob.checksum && activeJob.checksum !== reportChecksum && (
                            <div style={{ color: '#C05621', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <FaExclamationTriangle />
                                <span>Report updated since this email was prepared</span>
                            </div>
                        )}
                    </div>

                    {/* Section 1: Recipients List */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                        <div
                            onClick={() => setOpenRecipients(!openRecipients)}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px',
                                backgroundColor: '#f8fafc',
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>
                                <FaUserCheck style={{ color: '#176B64' }} />
                                <span>Recipients ({recipients.length})</span>
                            </div>
                            {openRecipients ? <FaChevronDown style={{ color: '#a0aec0' }} /> : <FaChevronRight style={{ color: '#a0aec0' }} />}
                        </div>

                        {openRecipients && (
                            <div style={{ padding: '16px' }}>
                                {recipients.length === 0 ? (
                                    <p style={{ margin: 0, fontSize: '13px', color: '#a0aec0' }}>No recipients selected.</p>
                                ) : (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#718096' }}>
                                                    <th style={{ padding: '8px 10px' }}>Email Address</th>
                                                    <th style={{ padding: '8px 10px' }}>Delivery State</th>
                                                    <th style={{ padding: '8px 10px' }}>Notes / Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recipients.map((r, idx) => {
                                                    const isWarn = ['REVIEW_REQUIRED', 'SKIPPED_INELIGIBLE'].includes(r.state);
                                                    const isAccepted = r.state === 'SMTP_ACCEPTED';
                                                    return (
                                                        <tr key={r.email || idx} style={{ borderBottom: '1px solid #edf2f7' }}>
                                                            <td style={{ padding: '10px', fontWeight: '600', color: '#2d3748' }}>
                                                                {r.email}
                                                            </td>
                                                            <td style={{ padding: '10px' }}>
                                                                <span style={{
                                                                    backgroundColor: isAccepted ? '#E5F2EF' : isWarn ? '#FFF5F5' : '#EDF2F7',
                                                                    color: isAccepted ? '#176B64' : isWarn ? '#C53030' : '#4A5568',
                                                                    padding: '3px 8px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '700',
                                                                }}>
                                                                    {isPreview ? 'Preview Candidate' : getLabel(r.state)}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '10px', color: isWarn ? '#C53030' : '#718096', fontSize: '12px' }}>
                                                                {r.reason ? getLabel(r.reason) : '—'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Section 2: Why Admins Were Excluded */}
                    {(excluded.length > 0 || !isKnownAudit) && (
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                            <div
                                onClick={() => setOpenExclusions(!openExclusions)}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    backgroundColor: '#f8fafc',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>
                                    <FaUserTimes style={{ color: '#E53E3E' }} />
                                    <span>Why Admins Were Excluded {isKnownAudit && `(${excluded.length})`}</span>
                                </div>
                                {openExclusions ? <FaChevronDown style={{ color: '#a0aec0' }} /> : <FaChevronRight style={{ color: '#a0aec0' }} />}
                            </div>

                            {openExclusions && (
                                <div style={{ padding: '16px' }}>
                                    {!isKnownAudit && (
                                        <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#B7791F' }}>
                                            This job has an incomplete selection audit. Some exclusion reasons may be unavailable.
                                        </p>
                                    )}
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#718096' }}>
                                                    <th style={{ padding: '8px 10px' }}>Admin ID</th>
                                                    <th style={{ padding: '8px 10px' }}>Email</th>
                                                    <th style={{ padding: '8px 10px' }}>Exclusion Reasons</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {excluded.map((row, idx) => (
                                                    <tr key={row.adminDocumentID || idx} style={{ borderBottom: '1px solid #edf2f7' }}>
                                                        <td style={{ padding: '10px', fontWeight: '600', color: '#4a5568' }}>
                                                            {row.adminDocumentID}
                                                        </td>
                                                        <td style={{ padding: '10px', color: '#2d3748' }}>
                                                            {row.email || 'No email recorded'}
                                                        </td>
                                                        <td style={{ padding: '10px', fontSize: '12px', color: '#718096' }}>
                                                            <div>
                                                                {(Array.isArray(row.reasons) ? row.reasons : []).map((r) => (
                                                                    <span key={r} style={{
                                                                        backgroundColor: '#FFF5F5',
                                                                        color: '#C53030',
                                                                        padding: '2px 6px',
                                                                        borderRadius: '4px',
                                                                        marginRight: '6px',
                                                                        display: 'inline-block',
                                                                        marginBottom: '2px',
                                                                    }}>
                                                                        {getLabel(r)}
                                                                    </span>
                                                                ))}
                                                                {row.duplicateOfAdminID && (
                                                                    <div style={{ marginTop: '4px', color: '#B7791F' }}>
                                                                        Duplicate email of: <code>{row.duplicateOfAdminID}</code>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Section 3: Preview Email Text */}
                    {activeJob.previewText && (
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                            <div
                                onClick={() => setOpenPreview(!openPreview)}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    backgroundColor: '#f8fafc',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>
                                    <FaEnvelope style={{ color: '#2b6cb0' }} />
                                    <span>Preview Email Content</span>
                                </div>
                                {openPreview ? <FaChevronDown style={{ color: '#a0aec0' }} /> : <FaChevronRight style={{ color: '#a0aec0' }} />}
                            </div>

                            {openPreview && (
                                <div style={{ padding: '16px' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <strong style={{ fontSize: '13px', color: '#4a5568' }}>Subject: </strong>
                                        <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: '600' }}>{activeJob.subject || 'Monthly Report'}</span>
                                    </div>
                                    <div style={{
                                        padding: '16px',
                                        backgroundColor: '#f7fafc',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        fontSize: '13px',
                                        lineHeight: '1.6',
                                        color: '#2d3748',
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace',
                                    }}>
                                        {activeJob.previewText}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Section 4: Audit Details & Log */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                        <div
                            onClick={() => setOpenAudit(!openAudit)}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px',
                                backgroundColor: '#f8fafc',
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>
                                <FaHistory style={{ color: '#718096' }} />
                                <span>Audit Details & Job Timestamps</span>
                            </div>
                            {openAudit ? <FaChevronDown style={{ color: '#a0aec0' }} /> : <FaChevronRight style={{ color: '#a0aec0' }} />}
                        </div>

                        {openAudit && (
                            <div style={{ padding: '16px', fontSize: '13px', color: '#4a5568' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                                    <div style={{ backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '6px', border: '1px solid #edf2f7' }}>
                                        <div style={{ fontSize: '11px', color: '#718096', fontWeight: '700', textTransform: 'uppercase' }}>Created</div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#2d3748', marginTop: '2px' }}>
                                            {formatTimestamp(activeJob.createdAtMs)}
                                        </div>
                                    </div>
                                    <div style={{ backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '6px', border: '1px solid #edf2f7' }}>
                                        <div style={{ fontSize: '11px', color: '#718096', fontWeight: '700', textTransform: 'uppercase' }}>Recipients Selected</div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#2d3748', marginTop: '2px' }}>
                                            {formatTimestamp(activeJob.selectedAtMs)}
                                        </div>
                                    </div>
                                    <div style={{ backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '6px', border: '1px solid #edf2f7' }}>
                                        <div style={{ fontSize: '11px', color: '#718096', fontWeight: '700', textTransform: 'uppercase' }}>Completed</div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#2d3748', marginTop: '2px' }}>
                                            {formatTimestamp(activeJob.completedAtMs)}
                                        </div>
                                    </div>
                                </div>
                                <p style={{ fontSize: '12px', color: '#718096', lineHeight: '1.5', margin: 0 }}>
                                    Selection decisions are recorded when the recipient list is prepared. A later eligibility change may appear as a skipped delivery without a detailed reason.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MonthlyEmailAuditSection;
